import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { CardsSkeleton } from '@/components/ui/skeleton';
import { apiClient } from '@/lib/api-client';
import { formatAmountMinor, resolveFinanceLocale } from './finance-format';
import { isNullableString, isRecord } from './finance-view-guards';
import { useFinanceView } from './useFinanceView';

type ReferenceCategory = {
  code: string;
  name: string;
  direction: 'inflow' | 'outflow';
  economicRelevant: boolean;
  costNature: string;
  groupLabel: string;
  incomeStatementLineCode: string | null;
  defaultVatRateBasisPoints: number;
  active: boolean;
  displayOrder: number;
  movementCount: number;
};

type ReferenceView = {
  organizationId: string;
  categories: ReferenceCategory[];
  incomeStatementLines: Array<{ code: string; label: string; displayOrder: number }>;
  incomeStatementComparatives: Array<{ lineCode: string; fiscalYear: number; amountMinor: number }>;
  bankAccounts: Array<{
    label: string;
    active: boolean;
    balances: Array<{ observedOn: string; amountMinor: number; note: string | null }>;
  }>;
  parameters: Array<{ key: string; value: string; description: string | null }>;
};

function isReferenceView(value: unknown): value is ReferenceView {
  if (!isRecord(value)) {
    return false;
  }
  return (
    typeof value.organizationId === 'string' &&
    Array.isArray(value.categories) &&
    value.categories.every(
      (category) =>
        isRecord(category) &&
        typeof category.code === 'string' &&
        typeof category.name === 'string' &&
        (category.direction === 'inflow' || category.direction === 'outflow') &&
        typeof category.economicRelevant === 'boolean' &&
        typeof category.costNature === 'string' &&
        typeof category.groupLabel === 'string' &&
        isNullableString(category.incomeStatementLineCode) &&
        typeof category.defaultVatRateBasisPoints === 'number' &&
        typeof category.active === 'boolean' &&
        typeof category.movementCount === 'number',
    ) &&
    Array.isArray(value.incomeStatementLines) &&
    value.incomeStatementLines.every(
      (line) => isRecord(line) && typeof line.code === 'string' && typeof line.label === 'string',
    ) &&
    Array.isArray(value.incomeStatementComparatives) &&
    value.incomeStatementComparatives.every(
      (comparative) =>
        isRecord(comparative) &&
        typeof comparative.lineCode === 'string' &&
        typeof comparative.fiscalYear === 'number' &&
        typeof comparative.amountMinor === 'number',
    ) &&
    Array.isArray(value.bankAccounts) &&
    value.bankAccounts.every(
      (account) =>
        isRecord(account) &&
        typeof account.label === 'string' &&
        typeof account.active === 'boolean' &&
        Array.isArray(account.balances) &&
        account.balances.every(
          (balance) =>
            isRecord(balance) &&
            typeof balance.observedOn === 'string' &&
            typeof balance.amountMinor === 'number' &&
            isNullableString(balance.note),
        ),
    ) &&
    Array.isArray(value.parameters) &&
    value.parameters.every(
      (parameter) =>
        isRecord(parameter) &&
        typeof parameter.key === 'string' &&
        typeof parameter.value === 'string' &&
        isNullableString(parameter.description),
    )
  );
}

/**
 * The registers, read back: the view that makes the other three auditable.
 *
 * Every figure on the income statement, the cash-flow grid and the job ledger is grouped,
 * reclassified and reconciled through these rows, and a category with no movements is worth seeing
 * on this screen - either it has fallen out of use, or the movements meant to point at it are
 * pointing somewhere else.
 */
function CategoriesSection({ categories }: { readonly categories: ReferenceView['categories'] }) {
  const { t } = useTranslation('finance');
  return (
    <div>
      <h2 className="text-base font-medium">{t('reference.categories.title')}</h2>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full min-w-3xl text-sm">
          <caption className="sr-only">{t('reference.categories.title')}</caption>
          <thead>
            <tr className="border-border border-b text-left">
              <th scope="col" className="py-2 font-medium">
                {t('reference.categories.code')}
              </th>
              <th scope="col" className="py-2 pl-3 font-medium">
                {t('reference.categories.name')}
              </th>
              <th scope="col" className="py-2 pl-3 font-medium">
                {t('reference.categories.direction')}
              </th>
              <th scope="col" className="py-2 pl-3 font-medium">
                {t('reference.categories.section')}
              </th>
              <th scope="col" className="py-2 pl-3 font-medium">
                {t('reference.categories.nature')}
              </th>
              <th scope="col" className="py-2 pl-3 font-medium">
                {t('reference.categories.group')}
              </th>
              <th scope="col" className="py-2 pl-3 font-medium">
                {t('reference.categories.line')}
              </th>
              <th scope="col" className="py-2 pl-3 text-right font-medium">
                {t('reference.categories.rate')}
              </th>
              <th scope="col" className="py-2 pl-3 text-right font-medium">
                {t('reference.categories.movements')}
              </th>
            </tr>
          </thead>
          <tbody>
            {categories.map((category) => (
              <tr key={category.code} className={category.active ? '' : 'text-foreground/50'}>
                <th scope="row" className="py-1.5 text-left font-normal">
                  {category.code}
                </th>
                <td className="py-1.5 pl-3">{category.name}</td>
                <td className="py-1.5 pl-3">{t(`reference.direction.${category.direction}`)}</td>
                <td className="py-1.5 pl-3">
                  {t(category.economicRelevant ? 'reference.sectionA' : 'reference.sectionB')}
                </td>
                <td className="py-1.5 pl-3">{category.costNature}</td>
                <td className="py-1.5 pl-3">{category.groupLabel}</td>
                <td className="py-1.5 pl-3">{category.incomeStatementLineCode ?? '—'}</td>
                <td className="py-1.5 pl-3 text-right tabular-nums">
                  {category.defaultVatRateBasisPoints / 100}%
                </td>
                <td className="py-1.5 pl-3 text-right tabular-nums">{category.movementCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AccountsSection({
  accounts,
  amount,
}: {
  readonly accounts: ReferenceView['bankAccounts'];
  readonly amount: (valueMinor: number) => string;
}) {
  const { t } = useTranslation('finance');
  return (
    <div>
      <h2 className="text-base font-medium">{t('reference.accounts.title')}</h2>
      <ul className="mt-2 space-y-2 text-sm">
        {accounts.map((account) => (
          <li key={account.label}>
            <span className="font-medium">{account.label}</span>
            {!account.active && (
              <span className="text-foreground/50"> · {t('reference.accounts.inactive')}</span>
            )}
            <ul className="text-foreground/70 mt-1 space-y-0.5 text-xs">
              {account.balances.length === 0 ? (
                <li>{t('reference.accounts.noBalances')}</li>
              ) : (
                account.balances.map((balance) => (
                  <li key={balance.observedOn}>
                    {balance.observedOn} · {amount(balance.amountMinor)}
                    {balance.note !== null && ` · ${balance.note}`}
                  </li>
                ))
              )}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function FinanceReferencePage() {
  const { t, i18n } = useTranslation('finance');
  const locale = resolveFinanceLocale(i18n.resolvedLanguage);

  const request = useCallback(() => apiClient.GET('/v1/finance/reference-registers', {}), []);
  const view = useFinanceView(request, isReferenceView);

  if (view.status === 'loading') {
    return <CardsSkeleton />;
  }

  if (view.status === 'error') {
    return (
      <div className="p-6">
        <p className="text-sm">{t('reference.loadError')}</p>
        <Button className="mt-3" onClick={view.reload}>
          {t('dashboard.retry')}
        </Button>
      </div>
    );
  }

  const data = view.data;
  const amount = (valueMinor: number) => formatAmountMinor(valueMinor, locale);
  const isEmpty =
    data.categories.length === 0 &&
    data.incomeStatementLines.length === 0 &&
    data.bankAccounts.length === 0 &&
    data.parameters.length === 0;

  return (
    <section className="space-y-8 p-6">
      <div>
        <h2 className="screen-title">{t('reference.title')}</h2>
        <p className="text-foreground/60 mt-1 text-sm">{t('reference.subtitle')}</p>
      </div>

      {isEmpty && <p className="text-sm">{t('reference.empty')}</p>}

      {data.categories.length > 0 && <CategoriesSection categories={data.categories} />}

      {data.incomeStatementLines.length > 0 && (
        <div>
          <h2 className="text-base font-medium">{t('reference.lines.title')}</h2>
          <ul className="mt-2 space-y-1 text-sm">
            {data.incomeStatementLines.map((line) => (
              <li key={line.code}>
                <span className="text-foreground/50">{line.code}</span> · {line.label}
                {data.incomeStatementComparatives
                  .filter((comparative) => comparative.lineCode === line.code)
                  .map((comparative) => (
                    <span key={comparative.fiscalYear} className="text-foreground/60">
                      {' '}
                      · {comparative.fiscalYear}: {amount(comparative.amountMinor)}
                    </span>
                  ))}
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.bankAccounts.length > 0 && (
        <AccountsSection accounts={data.bankAccounts} amount={amount} />
      )}

      {data.parameters.length > 0 && (
        <div>
          <h2 className="text-base font-medium">{t('reference.parameters.title')}</h2>
          <ul className="mt-2 space-y-1 text-sm">
            {data.parameters.map((parameter) => (
              <li key={parameter.key}>
                <span className="text-foreground/50">{parameter.key}</span> · {parameter.value}
                {parameter.description !== null && (
                  <span className="text-foreground/60"> · {parameter.description}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
